import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { capitalizeFirstLetterOfEachWord, getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PVMUrls } from 'config/urls';
import { colorConstants } from 'config/colorConstants';
import { ConfigKeys } from 'utils/Enums';
import _ from 'lodash';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any[];
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchFeatureTableData({ queryKey, variables = {}, queryConfig = {} }: Props): outputType {
    const { appConfig } = useContext(UserContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: PVMUrls.FeatureTableDataUrl,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        // if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            // if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData: any = parseTableData(payload);
            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchFeatureTableData };

const columnsToAddUnits = ['actuals', 'plan'];

function parseTableData(data: any) {
    const parsedData: any[] = [];
    data.forEach((row: any) => {
        const parsedRow: any = {
            label: capitalizeFirstLetterOfEachWord(row?.row_label?.replace(/_/g, ' ')),
        };

        // Iterate over each column
        row.columns.forEach((column: any) => {
            const updatedCol = column?.name || '';
            let nested: any = {};
            //iterate over values of columns
            Object.keys(column?.values)?.map((key: any) => {
                // capitalize the keys of nested keys
                nested[_.capitalize(key)] = {
                    pre: columnsToAddUnits.includes(key) ? column?.pre_unit : '',
                    post: columnsToAddUnits.includes(key) ? column?.post_unit : '',
                    value: column?.values?.[key],
                };
            });
            parsedRow[updatedCol] = { nested };
        });

        parsedData.push(parsedRow);
    });

    return parsedData;
}
